// Explicit bill-to-schedule comparison; confirmation archives only the planned entry and retains invoice evidence.
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import { useWorkspaceStore } from '@/stores/workspace';

interface Booking { id:string; merchant:string|null; receipt_date:string; currency:string; total_amount:number }
interface Preview {
  preview_id:string; scheduled:Booking; actual:Booking & { report_amount:number|null };
  impact:{household_aud_delta:number|null};
}
interface Props {
  receipt:{id:string;booking_evidence?:string|null;source?:string|null;merged_into_receipt_id?:string|null;bank_transaction_id?:string|null};
}

export function FixedBookingMatch({receipt}:Props) {
  const {i18n}=useTranslation(), zh=i18n.language.startsWith('zh');
  const ws=useWorkspaceStore(s=>s.current),qc=useQueryClient();
  const [open,setOpen]=useState(false),[selected,setSelected]=useState('');
  const scheduled=receipt.booking_evidence==='scheduled';
  const candidates=useQuery({queryKey:['fixed-booking',ws?.id,receipt.id],
    queryFn:()=>api.get<{candidates:Booking[];linked:null|{scheduled_receipt_id:string;merchant:string;receipt_date:string}}>(`/receipts/${receipt.id}/fixed-bookings`),
    enabled:!!ws&&!scheduled});
  const preview=useMutation({mutationFn:()=>api.post<Preview>(`/receipts/${receipt.id}/fixed-booking/preview`,{scheduled_receipt_id:selected})});
  const confirm=useMutation({mutationFn:(previewId:string)=>api.post(`/receipts/${receipt.id}/fixed-booking/confirm`,{preview_id:previewId}),
    onSuccess:async()=>{
      await Promise.all(['fixed-booking','receipt','receipts','insights','advisor','assistant','expense-groups'].map(key=>qc.invalidateQueries({queryKey:[key]})));
      preview.reset();setOpen(false);
    }});

  if(scheduled) return <section className="rounded-xl border border-blue-200 bg-blue-50 p-3 text-sm text-blue-950">
    <p className="font-medium">{zh?'按计划入账':'Booked as scheduled'}</p>
    <p className="mt-1 text-xs leading-relaxed">{receipt.merged_into_receipt_id
      ? <Link className="underline" to={`/receipts/${receipt.merged_into_receipt_id}`}>{zh?'已用真实账单核对，合计只计入真实账单。':'Matched to a real bill; only the bill counts in totals.'}</Link>
      : receipt.bank_transaction_id
        ? (zh?'按计划建立，已关联银行扣款。':'Created from the schedule and linked to a bank debit.')
        : (zh?'按设定的金额和日期记账，尚未核对账单或银行扣款。上传账单后，可在账单详情核对。':'Recorded at the planned amount and date, awaiting bill or bank evidence. Upload the bill and match it from its detail page.')}</p>
  </section>;

  if(candidates.data?.linked) return <section className="rounded-xl border bg-card p-3 text-sm">
    <p className="font-medium">{zh?'已核对固定支出':'Fixed expense matched'}</p>
    <Link className="mt-1 block text-xs underline" to={`/receipts/${candidates.data.linked.scheduled_receipt_id}`}>
      {candidates.data.linked.merchant} · {candidates.data.linked.receipt_date}
    </Link>
  </section>;
  if(!candidates.data?.candidates.length) return null;

  return <section className="space-y-3 rounded-xl border bg-card p-3 text-sm">
    <button type="button" className="font-medium underline" onClick={()=>setOpen(!open)}>{zh?'核对固定支出':'Match a fixed expense'}</button>
    {open&&<>
      <p className="text-xs text-muted-foreground">{zh?'如果这张账单对应已自动入账的固定支出，选择记录并预览，避免合计重复。':'If this bill corresponds to an automatically booked expense, choose and preview it to avoid counting twice.'}</p>
      <label className="block space-y-1 text-xs">
        <span>{zh?'对应的计划记录':'Matching scheduled entry'}</span>
        <select value={selected} onChange={e=>{setSelected(e.target.value);preview.reset();confirm.reset();}}
          className="w-full min-w-0 rounded-lg border bg-background px-2 py-2 text-sm">
          <option value="">{zh?'请选择':'Choose an entry'}</option>
          {candidates.data.candidates.map(c=><option key={c.id} value={c.id}>{c.merchant} · {c.receipt_date} · {formatCurrency(c.total_amount,c.currency)}</option>)}
        </select>
      </label>
      {!preview.data&&<button type="button" disabled={!selected||preview.isPending} onClick={()=>preview.mutate()}
        className="rounded-lg border px-3 py-2 disabled:opacity-50">{preview.isPending?'…':zh?'预览核对':'Preview match'}</button>}
      {preview.data&&<div className="space-y-2 rounded-lg bg-muted/50 p-3 text-xs">
        <p>{zh?'计划记录':'Scheduled'}: {preview.data.scheduled.merchant} · {preview.data.scheduled.receipt_date} · {formatCurrency(preview.data.scheduled.total_amount,preview.data.scheduled.currency)}</p>
        <p>{zh?'真实账单':'Real bill'}: {preview.data.actual.merchant} · {preview.data.actual.receipt_date} · {formatCurrency(preview.data.actual.total_amount,preview.data.actual.currency)}</p>
        {preview.data.actual.report_amount!==null&&<p>AUD {formatCurrency(preview.data.actual.report_amount,'AUD')}</p>}
        <p>{zh?'计划记录停止计入合计，保留这张真实账单及其银行、分组和原币金额。':'The planned entry will stop counting in totals. This bill, its bank links, group and original currency stay unchanged.'}</p>
        {preview.data.impact.household_aud_delta!==null&&<p>{zh?'合计减少':'Total reduction'}: {formatCurrency(-preview.data.impact.household_aud_delta,'AUD')}</p>}
        <button type="button" disabled={confirm.isPending} onClick={()=>confirm.mutate(preview.data!.preview_id)}
          className="w-full rounded-lg bg-foreground px-3 py-2 font-medium text-background disabled:opacity-50">{confirm.isPending?'…':zh?'确认是同一笔支出':'Confirm this is the same expense'}</button>
      </div>}
      {(preview.error||confirm.error)&&<p role="alert" className="text-xs text-destructive">{String((preview.error||confirm.error) instanceof Error?(preview.error||confirm.error)!.message:(preview.error||confirm.error))}</p>}
    </>}
  </section>;
}
